/**
 * tracks.ts — THE SCORES.
 *
 * Four background beds for the roulette table, each written to do a different
 * job to the player. They are DATA, not code: `music.ts` is a small engine that
 * can play any of them, so adding a fifth is a matter of writing music rather
 * than writing software.
 *
 * ─── WHAT ACTUALLY MAKES A GAME FEEL EXCITING ────────────────────────────────
 *
 * Worth stating plainly, because it decided the whole shape of this file.
 *
 * The instinct is to write a more energetic loop. That is a trap. A high-arousal
 * loop that never changes is the fastest way to get a player to mute the game:
 * arousal habituates, and once it has, the same music that was exciting at minute
 * one is merely loud at minute thirty. Real casino floors have known this for
 * decades — the ambient music is deliberately unremarkable and the excitement
 * lives in the EVENTS.
 *
 * What does not habituate is ANTICIPATION. The reward response to music peaks
 * before the resolution arrives, not on it — the craving is in the approach. So
 * the excitement here is built from three things, in this order of importance:
 *
 *   1. CONTOUR ACROSS THE ROUND. Every track is written to be played at a
 *      variable intensity, and `music.ts` drives that from the round's own
 *      phases: quiet while the window is open, tightening through the last five
 *      seconds, wide open while the ball is running, releasing on the result.
 *      The same bars do all of that — nothing is swapped, so nothing seams.
 *   2. HARMONIC PULL. A progression that constantly leans toward the next bar
 *      makes a player lean with it. Where a track wants that, it uses a real
 *      turnaround with a real dominant; where it wants calm, it circles instead.
 *      Each track declares which it is (`feel`), and the gate test checks that
 *      the harmony actually matches the claim.
 *   3. BRIGHTNESS. The single strongest timbral cue for arousal is high-frequency
 *      content. Intensity opens the ostinato's filter rather than raising the
 *      volume, so the bed gets KEENER instead of just louder.
 *
 * ─── THE RULES EVERY TRACK OBEYS ─────────────────────────────────────────────
 *
 *   - Four bars. Between 7 and 12 seconds a pass, so a 20 s betting window hears
 *     roughly two, and a 12 s quick window hears one and a bit.
 *   - THE TOP VOICE DOES NOT MOVE ACROSS THE LOOP JOIN. Bar 4's highest note is
 *     bar 1's highest note. This is the single rule that makes a loop unfindable:
 *     the voice the ear follows has no event at the seam.
 *   - Chord tones are listed low to high; the figure indexes into them. That is
 *     what makes these ostinatos rather than chord loops — the SHAPE is fixed and
 *     the harmony moves underneath it.
 *   - Nothing is louder than the chip sound. Music loses to the game, always.
 */

/* ─── pitches ────────────────────────────────────────────────────────────── */

const G2 = 98.00, Ab2 = 103.83, A2 = 110.00, B2 = 123.47, C3 = 130.81;
const D3 = 146.83, E3 = 164.81, F3 = 174.61, G3 = 196.00, Ab3 = 207.65;
const A3 = 220.00, Bb3 = 233.08, B3 = 246.94, C4 = 261.63, D4 = 293.66;
const E4 = 329.63, F4 = 349.23, G4 = 392.00, Ab4 = 415.30, A4 = 440.00;
const Bb4 = 466.16, B4 = 493.88, C5 = 523.25, D5 = 587.33, E5 = 659.25;
const G5 = 783.99, A5 = 880.00, C6 = 1046.50;
const F2 = 87.31;

export type Bar = {
  bass: number;
  /** the soft middle — third and fifth, or whatever colours the bar */
  pad: [number, number];
  /** the ostinato's chord tones, LOW TO HIGH. The figure indexes into these. */
  tones: [number, number, number, number];
};

export type Track = {
  id: string;
  /** shown on the Music tile */
  name: string;
  /** one line, plain language, for the picker */
  blurb: string;
  bpm: number;
  /** the tonic, in Hz — the gate test measures the harmony against it */
  tonic: number;
  /**
   * 'circling'  — no dominant, no leading tone. Never arrives, so it never wears
   *               out. The right choice for a long sitting.
   * 'arriving'  — a real turnaround with a real pull home. Gives a small lift
   *               every pass, at the cost of a loop point you could find if you
   *               went looking. The right choice for excitement.
   */
  feel: 'circling' | 'arriving';
  chart: [Bar, Bar, Bar, Bar];
  /** which of the bar's eight eighth-note slots carry a note */
  slots: number[];
  /** which chord tone each of those notes takes */
  shape: number[];
  /** relative velocity per note — this is where a figure gets its lilt */
  accent: number[];
  bells: { bar: number; slot: number; freq: number }[];
  /**
   * THE DRIVE VOICE. Only audible above half intensity, and it is what turns a
   * calm bed into an urgent one without changing a single note of the music: a
   * short low thump, `per` times a bar, felt more than heard.
   */
  pulse?: { freq: number; per: number };
  timbre: {
    osc: OscillatorType;
    /** filter cutoff at ZERO intensity; it opens from here as the round tightens */
    cut: number;
    /** how far the filter opens at full intensity, in Hz */
    open: number;
    /** pluck decay — short reads as percussive, long as singing */
    decay: number;
  };
  /** per-track level trim, so a sawtooth track does not shout over a sine one */
  trim: number;
};

/* ════════════════════════════════════════════════════════════════════════════
   1. GREEN FELT — the long sitting.
   ════════════════════════════════════════════════════════════════════════════
   A minor, 84 BPM. i – VI – III – v, no dominant anywhere: it circles and never
   arrives, which is exactly what you want from something playing for an hour.
   This is the calm end of the range and the safe default. Bars 1-3 hold C4 and E4
   completely still while the harmony moves under them.                        */
const GREEN_FELT: Track = {
  id: 'green-felt',
  name: 'Green Felt',
  blurb: 'Calm and unhurried. Best for long sessions.',
  bpm: 84, tonic: A2, feel: 'circling',
  chart: [
    { bass: A2, pad: [E3, C4], tones: [A3, C4, E4, B4] },   // Am9
    { bass: F2, pad: [C3, A3], tones: [A3, C4, E4, A4] },   // Fmaj7
    { bass: C3, pad: [G3, E4], tones: [G3, C4, E4, B4] },   // Cmaj7
    { bass: E3, pad: [B2, G3], tones: [G3, D4, E4, B4] },   // Em7 — B4 on top, held into bar 1
  ],
  slots: [0, 2, 3, 5, 7],
  shape: [0, 2, 1, 3, 2],
  accent: [1.0, 0.72, 0.80, 0.66, 0.84],
  bells: [{ bar: 0, slot: 0, freq: E5 }, { bar: 2, slot: 4, freq: G5 }],
  timbre: { osc: 'triangle', cut: 1700, open: 1400, decay: 0.55 },
  trim: 1.0,
};

/* ════════════════════════════════════════════════════════════════════════════
   2. MONTE CARLO — glamour, and the feeling that this is your night.
   ════════════════════════════════════════════════════════════════════════════
   C major, 104 BPM. I – vi – ii – V: a TURNAROUND, the oldest forward-pulling
   device in popular music, and it is here for exactly that reason. Every bar
   leans into the next and the last one leans hardest — G13 back to Cmaj9 is a
   real dominant resolution, so each pass ends with a small arrival.

   That is a deliberate trade. Green Felt hides its loop point; this one has one,
   about every nine seconds, and each time it lands it feels like something good
   just happened. Repeated arrival is the mechanism — the same reason a slot
   machine chimes on a two-coin return.

   Major throughout, and voiced high and open: bright timbre and major mode are
   the two most reliable cues for positive affect there are. The message is "you
   can win this", which is the one feeling this game is supposed to produce.     */
const MONTE_CARLO: Track = {
  id: 'monte-carlo',
  name: 'Monte Carlo',
  blurb: 'Bright and glamorous. Lifts every few seconds.',
  bpm: 104, tonic: C3, feel: 'arriving',
  chart: [
    { bass: C3, pad: [E3, G3], tones: [E4, G4, B4, D5] },   // Cmaj9
    { bass: A2, pad: [C4, E4], tones: [E4, G4, B4, C5] },   // Am9
    { bass: D3, pad: [F3, A3], tones: [E4, F4, A4, D5] },   // Dm9
    { bass: G2, pad: [B2, F3], tones: [E4, G4, B4, D5] },   // G13 — the pull home
  ],
  // Six notes a bar at a faster tempo: about fifty per cent busier than Green
  // Felt. Busy enough to feel like activity, sparse enough not to be a texture.
  slots: [0, 2, 3, 4, 6, 7],
  shape: [0, 1, 2, 3, 2, 1],
  accent: [1.0, 0.62, 0.78, 0.92, 0.70, 0.58],
  bells: [{ bar: 0, slot: 0, freq: C6 }, { bar: 3, slot: 6, freq: A5 }],
  pulse: { freq: C3, per: 4 },
  timbre: { osc: 'triangle', cut: 2100, open: 2100, decay: 0.40 },
  trim: 0.95,
};

/* ════════════════════════════════════════════════════════════════════════════
   3. KISMAT — the one that sounds like home.
   ════════════════════════════════════════════════════════════════════════════
   Coloured by CHARUKESHI: C D E F G Ab Bb. A major-sounding lower half with a
   flat sixth and flat seventh above it, which produces that particular
   hopeful-but-aching quality Hindi film music has lived on for seventy years.

   To be accurate about what this is: it is not a raga. A raga is a grammar — a
   way of approaching and leaving each note — and no four-bar loop performs one.
   This borrows the SCALE for its colour, which is honest and is what film
   composers themselves do when writing to picture.

   Built on a DRONE rather than a chord progression, because that is how the
   music it borrows from actually works: the bass never leaves C for all four
   bars, and the movement is entirely melodic — the flat 6 arriving in bar 2 is
   the emotional event of the piece. A motionless bass also means the loop join
   has nothing to hide.

   Why it belongs in this game: every other option here is a Western casino
   pastiche. Players in this market have heard this colour their whole lives, and
   familiarity is its own kind of pull — this is the one that will feel like
   THEIR table rather than a copy of someone else's.                            */
const KISMAT: Track = {
  id: 'kismat',
  name: 'Kismat',
  blurb: 'Warm and Indian in flavour. Familiar, with a pull to it.',
  bpm: 96, tonic: C3, feel: 'circling',
  chart: [
    { bass: C3, pad: [G3, C4], tones: [C4, E4, G4, Bb4] },    // home, flat 7 on top
    { bass: C3, pad: [G3, Ab3], tones: [E4, G4, Ab4, C5] },   // the flat 6 arrives
    { bass: C3, pad: [G3, Bb3], tones: [D4, F4, Ab4, Bb4] },  // furthest out
    { bass: C3, pad: [G3, E4], tones: [C4, D4, G4, Bb4] },    // drawn home, Bb4 held
  ],
  slots: [0, 2, 3, 5, 6],
  shape: [0, 2, 3, 1, 3],
  accent: [1.0, 0.70, 0.88, 0.64, 0.76],
  bells: [{ bar: 1, slot: 4, freq: C6 }, { bar: 3, slot: 2, freq: G5 }],
  pulse: { freq: C3, per: 4 },
  timbre: { osc: 'triangle', cut: 1500, open: 1700, decay: 0.70 },
  trim: 1.0,
};

/* ════════════════════════════════════════════════════════════════════════════
   4. IGNITION — pure activation. For quick tables and short sittings.
   ════════════════════════════════════════════════════════════════════════════
   A minor, 126 BPM, i – VI – III – VII. Fast, driving, and the most obviously
   "exciting" thing here — which is precisely why it is not the default.

   126 BPM sits above a resting heart rate and below a sprint, the band that reads
   as activated rather than frantic. The figure is eight notes a bar with a
   sawtooth attack and a very short decay, so it reads as PERCUSSION as much as
   pitch, and the drive voice runs eight to the bar rather than four.

   Every bar tops out on the same A4. At this tempo that insistence is the point:
   a fixed high note under moving harmony is a hook, and it is what will still be
   in a player's head after they close the app.

   HONEST WARNING, since it is my job to give you one: this is the track most
   likely to be turned off. It is superb for a twelve-second quick room and
   wearing across an hour at a twenty-second table. Ship it, do not default to
   it.                                                                          */
const IGNITION: Track = {
  id: 'ignition',
  name: 'Ignition',
  blurb: 'Fast and driving. Made for quick tables.',
  bpm: 126, tonic: A2, feel: 'circling',
  chart: [
    { bass: A2, pad: [E3, A3], tones: [A3, C4, E4, A4] },   // Am
    { bass: F2, pad: [C3, A3], tones: [A3, C4, F4, A4] },   // F
    { bass: C3, pad: [G3, E4], tones: [G3, C4, E4, A4] },   // C6
    { bass: G2, pad: [D3, B3], tones: [G3, B3, D4, A4] },   // Gadd9 → straight back
  ],
  slots: [0, 1, 2, 3, 4, 5, 6, 7],
  shape: [0, 2, 1, 3, 0, 2, 1, 3],
  accent: [1.0, 0.52, 0.74, 0.58, 0.88, 0.52, 0.70, 0.62],
  bells: [{ bar: 0, slot: 0, freq: A5 }, { bar: 2, slot: 0, freq: E5 }],
  pulse: { freq: A2, per: 8 },
  timbre: { osc: 'sawtooth', cut: 1200, open: 2600, decay: 0.26 },
  trim: 0.72,
};

export const TRACKS: Track[] = [GREEN_FELT, MONTE_CARLO, KISMAT, IGNITION];

export const DEFAULT_TRACK = 'monte-carlo';

export const trackById = (id: string | null) =>
  TRACKS.find((t) => t.id === id) ?? TRACKS.find((t) => t.id === DEFAULT_TRACK)!;

/** Seconds a single pass takes — used by the lobby copy and by the gate test. */
export const passSeconds = (t: Track) => (60 / t.bpm) * 4 * 4;
